import { Box, Card, Stack, styled, Typography } from '@mui/material';
import {
  BORDER_RADIUS_3XL,
  BORDER_RADIUS_XL,
  CitToken,
  COLOR_ACCENT,
  COLOR_SECONDARY,
  H1,
  Section,
  useBreakpoints,
} from 'shared';

import { Beam } from '../assets/Beam';
import { VerticalLine } from '../assets/VerticalLine';
import { heroCards } from '../constants';

const tokenWidthPx = 185;
const tokenVisibleHalfWidthPx = 65;
const rayHeightPx = 321;
const rayTopPx = 120;
const desktopHeroHeightPx = 560;
const desktopCardWidthPx = 320;
const desktopCardHeightPx = 252;

const BeamItem = styled(Box)({
  position: 'absolute',
  top: 'var(--citRaysTop)',
  width: 'var(--citRaysSlotW)',
  height: `${rayHeightPx}px`,
  overflow: 'hidden',
  pointerEvents: 'none',
});

export const BackgroundCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: desktopCardWidthPx,
  maxWidth: desktopCardWidthPx,
  minHeight: desktopCardHeightPx,
  height: desktopCardHeightPx,
  margin: '0 auto',
  padding: theme.spacing(3),
  borderRadius: BORDER_RADIUS_3XL,
  position: 'absolute',
  zIndex: 2,
  textAlign: 'center',

  [theme.breakpoints.down('md')]: {
    width: '100%',
    maxWidth: 650,
    minHeight: 190,
    height: 'auto',
    position: 'static',
  },

  [theme.breakpoints.down('sm')]: {
    borderRadius: BORDER_RADIUS_XL,
    padding: theme.spacing(3, 2),
  },
}));

const cardPlacementSx = {
  topLeft: { left: 0, top: 0 },
  bottomLeft: { left: 0, bottom: 0 },
  topRight: { right: 0, top: 0 },
  bottomRight: { right: 0, bottom: 0 },
} as const;

const TokenCitFeatureCard = ({ card }: { card: (typeof heroCards)[number] }) => {
  const { Icon } = card;

  return (
    <BackgroundCard
      sx={{
        ...cardPlacementSx[card.placement],
      }}
    >
      <Box
        sx={{
          width: 62,
          height: 62,
          borderRadius: '50%',
          bgcolor: COLOR_ACCENT,
          color: 'common.black',
          display: 'grid',
          placeItems: 'center',
          mb: { xs: 2.5, md: 2 },
        }}
      >
        <Icon sx={{ width: 28, height: 28, color: 'common.black' }} />
      </Box>
      <Typography
        variant="h5"
        component="h3"
        sx={{ mb: 1, fontSize: { xs: 18, md: 18 }, lineHeight: 1.25, fontWeight: 600 }}
      >
        {card.title}
      </Typography>
      <Typography
        sx={{ color: COLOR_SECONDARY, lineHeight: 1.35, mb: { xs: 3, md: 2.5 }, fontSize: 14, textAlign: 'center' }}
      >
        {card.description}
      </Typography>
      <Typography
        component="a"
        href={card.href}
        referrerPolicy="no-referrer"
        rel="noreferrer"
        target="_blank"
        sx={{
          textDecoration: 'underline',
          margin: '0 auto',
          color: COLOR_ACCENT,
          fontSize: 14,
        }}
      >
        Learn More
      </Typography>
    </BackgroundCard>
  );
};

export const TokenCitHero = () => {
  const { isDownMdLayout } = useBreakpoints();

  return (
    <Section sx={{ mb: { xs: 5, md: 6 } }}>
      <H1
        text="CRAT Impact Token (CIT)"
        textAlign="center"
        lineHeight="1.25"
        fontSize={{ xs: 32, sm: 52, md: 72 }}
        highlightedWords={[0, 1]}
      />
      <Typography
        variant="h4"
        textAlign="center"
        lineHeight="1.45"
        className="regular"
        sx={{ mt: { xs: 2, md: 3 }, fontSize: { xs: 18, md: 24 } }}
      >
        The Gamified Engagement Layer <br /> of the CratD2C Ecosystem
      </Typography>

      <Stack alignItems="center" spacing={{ xs: 4, md: 5 }} sx={{ mt: { xs: 2, md: 4 } }}>
        {isDownMdLayout ? (
          <Box
            sx={{
              width: '100%',
            }}
          >
            <Stack spacing={2.5} alignItems="center">
              <Box
                component="img"
                src={CitToken}
                alt="CIT token"
                sx={{
                  width: 165,
                  height: 'auto',
                  mb: 1.5,
                }}
              />
              {heroCards.map((card) => (
                <TokenCitFeatureCard key={card.title} card={card} />
              ))}
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: `${desktopHeroHeightPx}px`,
              position: 'relative',
              overflow: 'hidden',
              userSelect: 'none',
              '--citCardW': `${desktopCardWidthPx}px`,
              '--citTokenW': `${tokenWidthPx}px`,
              '--citTokenVisibleHalfW': `${tokenVisibleHalfWidthPx}px`,
              '--citRaysSlotW': 'max(0px, calc(50% - var(--citTokenVisibleHalfW) - var(--citCardW)))',
              '--citRaysTop': `${rayTopPx}px`,
            }}
          >
            {heroCards.map((card) => (
              <TokenCitFeatureCard key={card.title} card={card} />
            ))}

            <BeamItem
              aria-hidden="true"
              sx={{
                left: 'var(--citCardW)',
              }}
            >
              <Beam
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                }}
              />
            </BeamItem>

            <BeamItem
              aria-hidden="true"
              sx={{
                right: 'var(--citCardW)',
              }}
            >
              <Beam
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  transform: 'rotate(180deg)',
                }}
              />
            </BeamItem>
            <Box
              component="img"
              src={CitToken}
              alt="CIT token"
              sx={{
                position: 'absolute',
                width: 'var(--citTokenW)',
                height: '170px',
                left: '50%',
                top: `calc(var(--citRaysTop) + 75px)`,
                scale: 1.01,
                transform: 'translateX(-50%)',
                objectFit: 'contain',
                zIndex: 1,
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                left: '50%',
                top: `calc(var(--citRaysTop) + 255px)`,
                transform: 'translateX(-50%)',
                color: COLOR_SECONDARY,
                fontSize: 12,
                letterSpacing: 0,
                textTransform: 'uppercase',
                zIndex: 1,
              }}
            >
              Reward Layer
            </Typography>
            <VerticalLine
              aria-hidden="true"
              sx={{
                position: 'absolute',
                left: '50%',
                top: `calc(var(--citRaysTop) + 285px)`,
                transform: 'translateX(-50%)',
                display: 'block',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
          </Box>
        )}
      </Stack>
    </Section>
  );
};
